import React, { useState, useCallback, useMemo } from 'react';
import {
    Box, Typography, Container, Grid, Paper, Button, useTheme, Tabs, Tab, Card, CardContent, Divider, Chip, Tooltip, IconButton, List, ListItem, ListItemIcon, ListItemText, FormControl, Select, MenuItem, InputLabel, CardActions,
    FormControlLabel, 
    Switch,
    Dialog, // Added for mock functionality
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import {
    Notifications as NotificationsIcon, Settings as ConfigIcon, MarkEmailRead as ReadIcon, AccessTime as TimeIcon, 
    PriorityHigh as HighIcon, PriorityLow as LowIcon, Security as SecurityIcon, Money as FinanceIcon, 
    People as HRIcon, Edit as EditIcon, FilterList as FilterIcon, Add as AddIcon,
    Settings as SettingsIcon, Business as SystemIcon, CheckCircle as ApprovedIcon, Assignment as TaskIcon,
} from '@mui/icons-material';

// --- MOCK Data & Configuration ---

// Map notification types to their display Icon and Color
const TYPE_CONFIG = {
    System: { icon: SystemIcon, color: 'info' },
    Finance: { icon: FinanceIcon, color: 'success' },
    HR: { icon: HRIcon, color: 'warning' },
    // Define unique action icons
    Approve: { icon: ApprovedIcon, color: 'success' },
    'Complete Task': { icon: TaskIcon, color: 'info' },
    'View Patch Notes': { icon: EditIcon, color: 'primary' },
    'Review Budget': { icon: FinanceIcon, color: 'success' },
    'View Logs': { icon: SecurityIcon, color: 'error' },
};

// MOCK Data
const mockNotifications = [
    { id: 1, type: 'System', title: 'Critical Security Update Required', priority: 'High', time: '5 mins ago', read: false, action: 'View Patch Notes' },
    { id: 2, type: 'Finance', title: 'Q4 Budget Utilization Reached 90%', priority: 'Medium', time: '2 hours ago', read: false, action: 'Review Budget' },
    { id: 3, type: 'HR', title: 'New Leave Request from Ben Smith', priority: 'Medium', time: 'Yesterday', read: true, action: 'Approve' },
    { id: 4, type: 'System', title: 'Data Sync Job Failed (ID: 404)', priority: 'High', time: '2 days ago', read: true, action: 'View Logs' },
    { id: 5, type: 'HR', title: 'Employee Onboarding Task Due Today', priority: 'Low', time: '3 days ago', read: true, action: 'Complete Task' },
];

const mockAlertConfigs = [
    { id: 1, name: 'Critical System Error', trigger: 'If system health score drops < 10%', channel: 'Email, Slack', status: true, priority: 'High' },
    { id: 2, name: 'Large Expense Threshold', trigger: 'If expense claim > $5,000', channel: 'Email, App', status: true, priority: 'Medium' },
    { id: 3, name: 'Low License Count', trigger: 'If available licenses < 10%', channel: 'Email', status: false, priority: 'Low' },
];

// --- Custom Hook for Business Logic (Pro-Level Abstraction) ---
const useAlertsLogic = (initialNotifications, initialConfigs) => {
    const [notifications, setNotifications] = useState(initialNotifications);
    const [configs, setConfigs] = useState(initialConfigs);
    const [filterType, setFilterType] = useState('All');
    const [dialogState, setDialogState] = useState({ open: false, title: '', content: null });

    // Dialog handlers
    const handleOpenDialog = useCallback((title, content) => {
        setDialogState({ open: true, title, content });
    }, []);

    const handleCloseDialog = useCallback(() => {
        setDialogState({ open: false, title: '', content: null });
    }, []);

    // Use useCallback for handler functions for performance and stability
    const handleMarkRead = useCallback((id) => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: !n.read } : n));
    }, []);

    const handleMarkAllRead = useCallback(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    }, []);

    // FIX: Functional Action Handler
    const handleAction = useCallback((id, action) => {
        setNotifications(prev => prev.filter(n => n.id !== id)); // Remove notification upon action
        handleOpenDialog(`${action} Successful!`, `Notification ID ${id} has been processed.`);
    }, [handleOpenDialog]);

    const handleToggleConfig = useCallback((id) => {
        setConfigs(prev => prev.map(c => c.id === id ? { ...c, status: !c.status } : c));
    }, []);

    // FIX: Functional Edit/New Rule Handler
    const handleEditConfig = useCallback((config = null) => {
        const title = config ? `Edit Rule: ${config.name}` : 'Create New Alert Rule';
        const content = (
            <Box component="form" noValidate autoComplete="off">
                <TextField 
                    margin="dense" 
                    label="Rule Name" 
                    fullWidth 
                    defaultValue={config?.name || ''} 
                />
                <TextField 
                    margin="dense" 
                    label="Trigger Condition" 
                    fullWidth 
                    multiline 
                    rows={2} 
                    defaultValue={config?.trigger || 'If... then...'} 
                />
                <FormControl fullWidth margin="dense">
                    <InputLabel>Priority</InputLabel>
                    <Select defaultValue={config?.priority || 'Medium'} label="Priority">
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Low">Low</MenuItem>
                    </Select>
                </FormControl>
            </Box>
        );
        handleOpenDialog(title, content);
    }, [handleOpenDialog]);
    
    const handleOpenCustomFilter = useCallback(() => {
        const title = "Advanced Filter Builder";
        const content = (
            <Typography variant="body1">
                This is where the complex Custom Filter Builder UI would be rendered, allowing users to define filters based on priority, type, time, and keywords.
            </Typography>
        );
        handleOpenDialog(title, content);
    }, [handleOpenDialog]);

    // Use useMemo to filter notifications only when dependencies change (Performance)
    const filteredNotifications = useMemo(() => {
        if (filterType === 'All') return notifications;
        return notifications.filter(n => n.type === filterType);
    }, [notifications, filterType]);
    
    const unreadCount = notifications.filter(n => !n.read).length;
    const activeConfigCount = configs.filter(c => c.status).length;
    const notificationTypes = ['All', ...new Set(initialNotifications.map(n => n.type))];

    return {
        filteredNotifications,
        configs,
        filterType,
        notificationTypes,
        unreadCount,
        activeConfigCount,
        dialogState,
        setFilterType,
        handleMarkRead,
        handleMarkAllRead,
        handleAction,
        handleToggleConfig,
        handleEditConfig,
        handleCloseDialog,
        handleOpenCustomFilter,
    };
};

// --- Sub-Component: Individual Notification Item (Highly Modular) ---
const NotificationItem = React.memo(({ notif, handleMarkRead, handleAction, theme }) => {
    const TypeIconComponent = TYPE_CONFIG[notif.type]?.icon || NotificationsIcon;
    const ActionIconComponent = TYPE_CONFIG[notif.action]?.icon || EditIcon;
    const actionColor = TYPE_CONFIG[notif.action]?.color || 'primary';
    const isHighPriority = notif.priority === 'High';

    return (
        <Box>
            <ListItem 
                sx={{ 
                    py: 2, 
                    px: 3,
                    bgcolor: notif.read ? theme.palette.grey[50] : theme.palette.primary.light + '1A',
                    borderLeft: `5px solid ${notif.read ? theme.palette.grey[200] : theme.palette.primary.main}`,
                    transition: 'background-color 0.3s, transform 0.2s',
                    '&:hover': { 
                        bgcolor: notif.read ? theme.palette.grey[100] : theme.palette.primary.light + '33',
                        transform: 'translateY(-2px)'
                    },
                }}
            >
                <ListItemIcon>
                    <TypeIconComponent color={TYPE_CONFIG[notif.type]?.color} sx={{ fontSize: '1.75rem' }} />
                </ListItemIcon>
                
                <ListItemText 
                    primary={
                        <Box display="flex" alignItems="center">
                            <Typography variant="subtitle1" fontWeight={notif.read ? 500 : 700} color={notif.read ? 'text.secondary' : 'text.primary'}>
                                {notif.title}
                            </Typography>
                            <Chip 
                                label={notif.priority} 
                                size="small" 
                                color={isHighPriority ? 'error' : 'warning'} 
                                icon={isHighPriority ? <HighIcon sx={{ fontSize: 16 }} /> : <TimeIcon sx={{ fontSize: 16 }} />}
                                sx={{ fontWeight: 700, ml: 2, height: 20 }}
                            />
                        </Box>
                    }
                    secondary={
                        <Box display="flex" justifyContent="space-between" pr={1}>
                            <Typography variant="caption" color="text.secondary">**Type:** {notif.type} • {notif.time}</Typography>
                        </Box>
                    }
                />
                
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 2 }}>
                    <Button 
                        size="small" 
                        variant="contained" 
                        color={actionColor} // Dynamic color based on action type
                        startIcon={<ActionIconComponent />}
                        sx={{ ml: 1, fontWeight: 600, minWidth: 100 }} 
                        onClick={() => handleAction(notif.id, notif.action)} // Pass ID for removal
                    >
                        {notif.action}
                    </Button>
                    <Tooltip title={notif.read ? "Mark Unread" : "Mark Read"}>
                        <IconButton edge="end" aria-label="read" onClick={() => handleMarkRead(notif.id)} sx={{ ml: 0.5 }}>
                            <ReadIcon color={notif.read ? 'success' : 'action'} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </ListItem>
            <Divider variant="inset" component="li" />
        </Box>
    );
});

// --- Sub-Component: Notification Inbox List ---
const NotificationInboxList = ({ notifications, theme, handleMarkRead, handleAction, filterType, notificationTypes, setFilterType }) => {
    
    // Helper to get chip color for filter display
    const getFilterChipColor = (type) => type === 'All' ? 'default' : TYPE_CONFIG[type]?.color;

    return (
        <Card elevation={10} sx={{ borderRadius: 4, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* FIX: Set a solid background on the CardContent to prevent overlay issues */}
            <CardContent sx={{ bgcolor: theme.palette.primary.main, color: 'white', py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                    <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Alerts Feed ({notifications.length})
                </Typography>
                
                {/* Filter Dropdown */}
                <FormControl variant="outlined" size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="filter-select-label" sx={{ color: theme.palette.primary.dark, fontWeight: 600 }}>Filter</InputLabel>
                    <Select
                        labelId="filter-select-label"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        label="Filter"
                        // FIX: Ensure dropdown input has white background
                        sx={{ bgcolor: 'white' }}
                        MenuProps={{
                            // Ensure the menu itself sits above content
                            PaperProps: { style: { zIndex: 10 } }
                        }}
                    >
                        {notificationTypes.map(type => (
                            <MenuItem key={type} value={type}>
                                <Chip 
                                    label={type} 
                                    size="small" 
                                    color={getFilterChipColor(type)} 
                                    sx={{ fontWeight: 600 }} 
                                />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </CardContent>
            
            <List sx={{ p: 0, flexGrow: 1, overflowY: 'auto' }}>
                {notifications.length > 0 ? (
                    notifications.map((notif) => (
                        <NotificationItem 
                            key={notif.id}
                            notif={notif}
                            handleMarkRead={handleMarkRead}
                            handleAction={handleAction}
                            theme={theme}
                        />
                    ))
                ) : (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" color="text.secondary">All Clear! 😌</Typography>
                        <Typography variant="body2" color="text.hint">No {filterType !== 'All' ? `${filterType} ` : ''}notifications match the current filter. Completed items are removed.</Typography>
                    </Box>
                )}
            </List>
        </Card>
    );
};

// --- Sub-Component: Alert Configuration Card ---
const AlertConfigCard = React.memo(({ config, theme, handleToggle, handleEdit }) => {
    const isHighPriority = config.priority === 'High';

    return (
        <Card 
            elevation={6} 
            sx={{ 
                borderRadius: 3, 
                p: 2, 
                transition: 'all 0.3s',
                borderLeft: `6px solid ${isHighPriority ? theme.palette.error.main : theme.palette.warning.main}`,
                '&:hover': { 
                    boxShadow: theme.shadows[15],
                    transform: 'translateY(-4px)'
                },
                opacity: config.status ? 1 : 0.6,
            }}
        >
            <CardContent sx={{ p: '8px !important' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" fontWeight={700} color="primary.dark" noWrap>{config.name}</Typography>
                    <Chip 
                        label={config.priority} 
                        size="small" 
                        color={isHighPriority ? 'error' : 'warning'} 
                        sx={{ fontWeight: 700, ml: 1 }} 
                    />
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mb: 2, height: 40, overflow: 'hidden' }}>
                    **Trigger:** {config.trigger}
                </Typography>
                
                <Divider sx={{ my: 1 }} />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Tooltip title={config.status ? "Click to Disable" : "Click to Enable"}>
                        <FormControlLabel
                            control={<Switch checked={config.status} onChange={() => handleToggle(config.id)} color="success" size="small" />}
                            label={<Typography variant="body2" fontWeight={600} color={config.status ? 'success.main' : 'text.secondary'}>{config.status ? "Active" : "Inactive"}</Typography>}
                            labelPlacement="start"
                            sx={{ m: 0 }}
                        />
                    </Tooltip>
                    <Typography variant="caption" color="text.primary">
                        **Channels:** {config.channel}
                    </Typography>
                </Box>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end', p: 1, borderTop: `1px solid ${theme.palette.grey[100]}` }}>
                <Button size="small" startIcon={<EditIcon />} onClick={() => handleEdit(config)} sx={{ fontWeight: 600 }}>
                    Edit Rule
                </Button>
            </CardActions>
        </Card>
    );
});


// --- Main Alerts & Notifications Component ---
const AlertsNotifications = () => {
    const theme = useTheme();
    const [activeTab, setActiveTab] = useState('inbox');
    
    // Use the custom hook to manage all state and logic
    const {
        filteredNotifications,
        configs,
        filterType,
        notificationTypes,
        unreadCount,
        activeConfigCount,
        dialogState, // Dialog State
        setFilterType,
        handleMarkRead,
        handleMarkAllRead,
        handleAction,
        handleToggleConfig,
        handleEditConfig,
        handleCloseDialog, // Dialog Close Handler
        handleOpenCustomFilter, // Custom Filter Handler
    } = useAlertsLogic(mockNotifications, mockAlertConfigs);

    return (
        <Container maxWidth="xl" sx={{ py: 4, minHeight: '100vh', bgcolor: theme.palette.grey[50] }}>
            {/* Header with Animation */}
            <Box 
                sx={{ 
                    mb: 4, 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    animation: 'headerSlide 0.8s ease-out forwards',
                    '@keyframes headerSlide': { 'from': { opacity: 0, transform: 'translateY(-10px)' }, 'to': { opacity: 1, transform: 'translateY(0)' } }
                }}
            >
                <Typography 
                    variant="h4" 
                    component="h1" 
                    fontWeight={800} 
                    color="primary.dark"
                    sx={{ fontSize: { xs: '1.75rem', md: '2.5rem' } }}
                >
                    <NotificationsIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 'inherit' }} />
                    System Monitoring Center
                </Typography>
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<ReadIcon />} 
                    onClick={handleMarkAllRead}
                    sx={{ px: 3, py: 1, fontWeight: 700, borderRadius: 2 }}
                    disabled={unreadCount === 0}
                >
                    Mark All Read ({unreadCount})
                </Button>
            </Box>
            
            <Divider sx={{ mb: 4 }} />

            {/* Tabs for Dual View */}
            <Paper elevation={4} sx={{ mb: 4, borderRadius: 3, overflow: 'hidden' }}>
                <Tabs 
                    value={activeTab} 
                    onChange={(e, v) => setActiveTab(v)} 
                    indicatorColor="primary" 
                    textColor="primary" 
                    variant="fullWidth" 
                    aria-label="Alerts and Configuration Tabs"
                    sx={{ 
                        borderBottom: 1, 
                        borderColor: 'divider',
                        '& .MuiTab-root': { py: 1.5 },
                    }}
                >
                    <Tab 
                        value="inbox" 
                        label={`Alert Inbox (${unreadCount})`} 
                        icon={<NotificationsIcon />} 
                        iconPosition="start"
                        sx={{ fontWeight: 700 }}
                    />
                    <Tab 
                        value="config" 
                        label={`Configuration (${activeConfigCount} Active)`} 
                        icon={<ConfigIcon />} 
                        iconPosition="start"
                        sx={{ fontWeight: 700 }}
                    />
                </Tabs>
            </Paper>

            {/* Tab Content */}
            <Box
                sx={{
                    transition: 'opacity 0.4s ease-in-out',
                    opacity: 1,
                }}
            >
                {/* 1. Notification Inbox View */}
                {activeTab === 'inbox' && (
                    <Grid container spacing={4}>
                        <Grid item xs={12} lg={9} sx={{ height: '70vh' }}>
                            <NotificationInboxList 
                                notifications={filteredNotifications} 
                                theme={theme}
                                handleMarkRead={handleMarkRead} 
                                handleAction={handleAction} 
                                filterType={filterType}
                                notificationTypes={notificationTypes}
                                setFilterType={setFilterType}
                            />
                        </Grid>
                        {/* Summary / Filters Sidebar */}
                        <Grid item xs={12} lg={3}>
                            <Card elevation={8} sx={{ p: 3, borderRadius: 4, position: 'sticky', top: theme.spacing(4) }}>
                                <Typography variant="h6" fontWeight={700} color="secondary.main" mb={2}>
                                    Overview
                                </Typography>
                                <Divider sx={{ mb: 2 }} />
                                <Box>
                                    <SummaryItem 
                                        label="Unread Alerts" 
                                        value={unreadCount} 
                                        color="primary" 
                                        icon={ReadIcon} 
                                    />
                                    <SummaryItem 
                                        label="High Priority" 
                                        value={mockNotifications.filter(n => n.priority === 'High').length} 
                                        color="error" 
                                        icon={HighIcon} 
                                    />
                                    <SummaryItem 
                                        label="Active Rules" 
                                        value={activeConfigCount} 
                                        color="success" 
                                        icon={SettingsIcon} 
                                    />
                                </Box>
                                {/* FIX: Custom Filter Builder button now opens a mock dialog */}
                                <Button 
                                    fullWidth 
                                    variant="outlined" 
                                    sx={{ mt: 3, fontWeight: 700 }} 
                                    startIcon={<FilterIcon />}
                                    onClick={handleOpenCustomFilter}
                                >
                                    Custom Filter Builder
                                </Button>
                            </Card>
                        </Grid>
                    </Grid>
                )}

                {/* 2. Alert Configuration View */}
                {activeTab === 'config' && (
                    <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                            <Typography variant="h5" fontWeight={700} color="secondary.dark">
                                Managed Rules ({activeConfigCount} Active)
                            </Typography>
                            {/* FIX: New Alert Rule button now opens a mock dialog */}
                            <Button 
                                variant="contained" 
                                color="secondary" 
                                startIcon={<AddIcon />} 
                                onClick={() => handleEditConfig(null)}
                                sx={{ fontWeight: 700, px: 3, py: 1 }}
                            >
                                New Alert Rule
                            </Button>
                        </Box>
                        
                        <Grid container spacing={3}>
                            {configs.map(config => (
                                <Grid item xs={12} sm={6} lg={4} key={config.id}>
                                    <AlertConfigCard 
                                        config={config} 
                                        theme={theme} 
                                        handleToggle={handleToggleConfig} 
                                        handleEdit={handleEditConfig} // Edit button opens dialog
                                    />
                                </Grid>
                            ))}
                            {/* Placeholder for New Rule CTA */}
                            <Grid item xs={12} sm={6} lg={4}>
                                <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: 3, textAlign: 'center', display: 'flex', flexDirection: 'column', justifyContent: 'center', border: `2px dashed ${theme.palette.secondary.light}` }}>
                                    <Typography variant="h6" color="text.secondary" mb={2} fontWeight={600}>
                                        Automate your workflow.
                                    </Typography>
                                    {/* FIX: Create New Rule button now opens a mock dialog */}
                                    <Button 
                                        variant="outlined" 
                                        startIcon={<AddIcon />} 
                                        size="large" 
                                        onClick={() => handleEditConfig(null)}
                                        sx={{ fontWeight: 700 }}
                                    >
                                        Create New Rule
                                    </Button>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Box>

            {/* Global Mock Dialog for Functionality */}
            <Dialog 
                open={dialogState.open} 
                onClose={handleCloseDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ fontWeight: 700, color: 'primary.dark' }}>
                    {dialogState.title}
                </DialogTitle>
                <DialogContent dividers>
                    {dialogState.content}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="secondary">
                        Close
                    </Button>
                    {(dialogState.title.includes('Edit Rule') || dialogState.title.includes('Create New')) && (
                        <Button onClick={handleCloseDialog} color="primary" variant="contained">
                            Save Changes
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Container>
    );
};

// --- Reusable Summary Item (Helper Component) ---
const SummaryItem = ({ label, value, color, icon: IconComponent }) => (
    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5} sx={{ p: 1, borderRadius: 1, '&:hover': { bgcolor: 'grey.100' } }}>
        <Box display="flex" alignItems="center">
            <IconComponent color={color} sx={{ mr: 1 }} />
            <Typography variant="body1" color="text.primary" fontWeight={600}>{label}</Typography>
        </Box>
        <Chip label={value} color={color} sx={{ fontWeight: 700 }} />
    </Box>
);

export default AlertsNotifications;